import { useState, useEffect, useCallback } from 'react';
import {
  getOutboundCallDetails,
  getOutcomeLabel,
  getOutcomeColorClasses,
  OutboundCallOutcome,
  isAlternativeAccepted,
  getAcceptedAlternativeNumber,
  fetchOutboundCallAudio,
} from '../../services/outboundCalls';
import CallOutcomeDisplay from './CallOutcomeDisplay';
import AudioPlayButton from '../consultationRecorder/AudioPlayButton';

/**
 * Modal to display a summary of what happened during an outbound call.
 * Shows outcome, patient response, call duration, and alternative times if applicable.
 */
export default function CallSummaryModal({
  isOpen,
  onClose,
  outboundCallId,
  appointmentData,
}) {
  const [callDetails, setCallDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && outboundCallId) {
      fetchCallDetails();
    }
  }, [isOpen, outboundCallId]);

  const fetchCallDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const details = await getOutboundCallDetails(outboundCallId, false);
      setCallDetails(details);
    } catch (err) {
      console.error('Failed to fetch call details:', err);
      setError('Failed to load call details');
    } finally {
      setLoading(false);
    }
  };

  // Wrapper function for fetching outbound call audio
  // AudioPlayButton expects (conversationId) => Promise, but we use callId instead
  const fetchAudioForOutboundCall = useCallback(async () => {
    return fetchOutboundCallAudio(outboundCallId);
  }, [outboundCallId]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Get patient response from outcome history
  const outcomeHistory = callDetails?.outcome_history || [];
  const latestOutcome = outcomeHistory[outcomeHistory.length - 1];
  const patientResponse = latestOutcome?.patient_response;
  const confidenceScore = latestOutcome?.confidence_score;

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // Format date/time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  // Get selected alternative time display
  const getSelectedAlternativeDisplay = () => {
    if (!callDetails?.outcome || !isAlternativeAccepted(callDetails.outcome)) {
      return null;
    }
    const altNumber = getAcceptedAlternativeNumber(callDetails.outcome);
    const altTimes = callDetails.alternative_times || [];
    if (altNumber && altTimes[altNumber - 1]) {
      const alt = altTimes[altNumber - 1];
      return {
        number: altNumber,
        time: formatDateTime(alt.start),
      };
    }
    return null;
  };

  const selectedAlt = getSelectedAlternativeDisplay();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Call Summary</h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <p className="text-red-600 text-sm">{error}</p>
              <button
                onClick={fetchCallDetails}
                className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Try again
              </button>
            </div>
          ) : callDetails ? (
            <>
              {/* Patient Name */}
              {appointmentData?.patient_name && (
                <div className="flex items-center gap-3 pb-3 border-b border-gray-100">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{appointmentData.patient_name}</p>
                    <p className="text-xs text-gray-500">Patient</p>
                  </div>
                </div>
              )}

              {/* Outcome */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Outcome
                </label>
                <div className="mt-1.5">
                  <CallOutcomeDisplay outcome={callDetails.outcome} size="md" />
                </div>
              </div>

              {/* Patient Response */}
              {patientResponse && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Patient Response
                  </label>
                  <p className="mt-1.5 text-sm text-gray-700 bg-gray-50 rounded-lg p-3 italic">
                    "{patientResponse}"
                  </p>
                </div>
              )}

              {/* Selected Alternative Time */}
              {selectedAlt && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    New Appointment Time
                  </label>
                  <div className="mt-1.5 flex items-center gap-2">
                    <svg className="h-4 w-4 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm text-gray-900">
                      Alternative {selectedAlt.number}: {selectedAlt.time}
                    </span>
                  </div>
                </div>
              )}

              {/* Call Details Grid */}
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Duration
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDuration(callDetails.call_duration_seconds)}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Call Time
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDateTime(callDetails.call_completed_at || callDetails.call_initiated_at)}
                  </p>
                </div>
              </div>

              {/* Call Recording */}
              {callDetails.conversation_id && outboundCallId && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Call Recording
                  </label>
                  <div className="mt-1.5">
                    <AudioPlayButton
                      conversationId={callDetails.conversation_id}
                      hasRecording={callDetails.has_recording}
                      pending={callDetails.recording_pending}
                      fetchAudioFn={fetchAudioForOutboundCall}
                    />
                  </div>
                </div>
              )}

              {/* Alternative Times Offered */}
              {callDetails.alternative_times && callDetails.alternative_times.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Alternative Times Offered
                  </label>
                  <ul className="mt-1.5 space-y-1">
                    {callDetails.alternative_times.map((alt, index) => (
                      <li
                        key={index}
                        className={`text-sm flex items-center gap-2 ${
                          selectedAlt?.number === index + 1
                            ? 'text-sky-700 font-medium'
                            : 'text-gray-600'
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full bg-gray-100 text-xs flex items-center justify-center">
                          {index + 1}
                        </span>
                        {formatDateTime(alt.start)}
                        {selectedAlt?.number === index + 1 && (
                          <span className="text-xs bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">
                            Selected
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Confidence Score (subtle) */}
              {confidenceScore !== null && confidenceScore !== undefined && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    AI Confidence: {Math.round(confidenceScore * 100)}%
                  </p>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
